import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Sparkles, Shield, Clock, FileText, Video } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import RazorPayButton from '@/components/Payment/RazorPayButton';
import { setEntitlementActive, fetchServerEntitlement } from '@/utils/secondOpinionAccess';
import { toast } from '@/hooks/use-toast';

/** Must match backend payments/plans.py second_opinion catalog. */
export const EXPERT_SO_PRICE = 99;
export const EXPERT_SO_CURRENCY = 'USD';

export default function SecondOpinionPaywallModal({
  open,
  onClose,
  reason = 'limit', // 'limit' | 'cta' | 'expert' | 'route-guard'
  onPaid,
  expertPlan,
}) {
  const navigate = useNavigate();
  const [paymentLoading, setPaymentLoading] = useState(false);

  const amount = expertPlan?.display_amount || EXPERT_SO_PRICE;
  const currency = expertPlan?.currency || EXPERT_SO_CURRENCY;

  useEffect(() => {
    if (!open) setPaymentLoading(false);
  }, [open]);

  if (!open) return null;

  const isExpert = reason === 'expert' || reason === 'cta';

  const handlePaymentSuccess = async (verificationData) => {
    try {
      setEntitlementActive('razorpay', verificationData.order_id || verificationData.payment_id);
      await fetchServerEntitlement();
      toast({
        title: 'Payment confirmed',
        description: isExpert
          ? 'You can now submit your expert written second-opinion request.'
          : 'Unlimited second-opinion reviews are unlocked with this purchase path via PRO, or continue your expert request.',
      });
      if (onPaid) {
        onPaid(verificationData);
      } else {
        navigate('/second-opinion', { replace: true, state: { openExpert: true } });
        onClose();
      }
    } catch (error) {
      console.error('Error processing payment success:', error);
      toast({
        variant: 'error',
        title: 'Payment error',
        description: 'Payment succeeded but we could not refresh access. Refresh the page or contact support.',
      });
    } finally {
      setPaymentLoading(false);
    }
  };

  const handlePaymentError = (error) => {
    console.error('Payment error:', error);
    const errorMessage = String(error?.message || error);
    if (errorMessage.includes('cancelled') || errorMessage.includes('Payment cancelled')) {
      toast({
        variant: 'info',
        title: 'Payment cancelled',
        description: 'You can retry anytime.',
      });
    } else {
      toast({
        variant: 'error',
        title: 'Payment failed',
        description: errorMessage,
      });
    }
    setPaymentLoading(false);
  };

  const title =
    reason === 'expert'
      ? 'Expert written second opinion'
      : reason === 'cta'
        ? 'Get a second opinion from an oncologist'
        : reason === 'limit'
          ? "You've used your free case questions"
          : 'Complete payment to continue';

  const description =
    reason === 'limit'
      ? 'Upgrade to ByOnco PRO for unlimited case briefs, or purchase a one-time expert written review (target within 12 hours).'
      : 'A board-certified oncology reviewer prepares a written second opinion on your case, target turnaround within 12 hours.';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-forest-ink/40 p-4"
      style={{ fontFamily: "'Instrument Sans', system-ui, sans-serif" }}
    >
      <Card className="w-full max-w-2xl bg-paper border-hair rounded-3xl">
        <CardHeader className="relative px-6 pt-6 pb-4">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-[#3d4a42] hover:text-forest-ink transition-colors"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>

          <div className="flex items-center justify-center mb-4">
            <div className="bg-mist border border-hair rounded-full h-16 w-16 flex items-center justify-center">
              <Sparkles className="h-8 w-8 text-forest-ink" />
            </div>
          </div>

          <CardTitle className="text-2xl sm:text-3xl text-center text-forest-ink mb-2 font-semibold">
            {title}
          </CardTitle>

          <CardDescription className="text-center text-[#3d4a42] text-base">
            {description}
          </CardDescription>
        </CardHeader>

        <CardContent className="px-6 pb-6 space-y-6">
          <div className="space-y-3">
            {[
              {
                icon: <FileText className="h-5 w-5 text-forest-ink" />,
                text: 'Written analysis from a board-certified oncology reviewer',
              },
              {
                icon: <Clock className="h-5 w-5 text-forest-ink" />,
                text: 'Target response within 12 hours',
              },
              {
                icon: <Shield className="h-5 w-5 text-forest-ink" />,
                text: 'Private case intake with confirmation by email',
              },
              {
                icon: <Video className="h-5 w-5 text-forest-ink" />,
                text: 'Optional live video consult available separately',
              },
            ].map((benefit, idx) => (
              <div key={idx} className="flex items-start gap-3 text-forest-ink">
                {benefit.icon}
                <span className="text-sm sm:text-base">{benefit.text}</span>
              </div>
            ))}
          </div>

          <div className="bg-mist border border-hair rounded-2xl p-4 text-center">
            <div className="text-3xl font-light text-forest-ink mb-1">
              {currency === 'USD' ? '$' : `${currency} `}
              {Number(amount).toLocaleString(currency === 'INR' ? 'en-IN' : 'en-US')}
            </div>
            <div className="text-sm text-[#3d4a42]">
              One-time payment for an expert written second opinion
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1">
              <RazorPayButton
                amount={Number(amount)}
                currency={currency}
                description="Expert Written Second Opinion"
                serviceType="second_opinion"
                onSuccess={handlePaymentSuccess}
                onError={handlePaymentError}
                className="w-full bg-forest-ink hover:bg-forest-shadow text-cream-paper"
                buttonText={paymentLoading ? 'Processing…' : `Pay ${currency === 'USD' ? '$' : ''}${amount}`}
              />
            </div>
            {reason === 'limit' && (
              <Button
                variant="outline"
                onClick={() => {
                  onClose();
                  navigate('/subscribe?redirect=/second-opinion');
                }}
                className="border-hair text-forest-ink hover:bg-mist"
              >
                Get ByOnco PRO
              </Button>
            )}
          </div>

          <p className="text-xs text-[#3d4a42] text-center">
            Secure payment powered by Razorpay. Educational and clinical review support, not an emergency service.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
